/**
 * @module user.transformer
 */
import { serverStatuses } from '~/config/settings/user.settings';
import { permissionsFrom } from './permission.transformer';

const userFrom = (rawUser) => {
  // If the user has no first or last name, use the email for name so that
  // functions requiring a list of names at least has a value.
  const name =
    rawUser.firstName || rawUser.lastName
      ? `${rawUser.firstName} ${rawUser.lastName}`
      : rawUser.email?.substring(0, rawUser.email?.lastIndexOf('@'));

  const rawGlobalActions = rawUser.globalActions || [];
  const rawSurveyTypePermissions = rawUser.permissions || [];
  const permissions = permissionsFrom(rawGlobalActions, rawSurveyTypePermissions);

  const user = {
    id: rawUser.id,
    userRoleId: rawUser.userRoleId,
    employeeId: rawUser.employeeId,
    name,
    firstName: rawUser.firstName,
    lastName: rawUser.lastName,
    email: rawUser.email,
    status: serverStatuses[rawUser.status],
    lang: rawUser.preferredLanguage?.langISOCode,
    avatarUrl: rawUser.avatar,
    isAdmin: Boolean(rawUser.worktangoAdmin),
    updatedAt: rawUser.updated ? new Date(rawUser.updated * 1000).toString() : undefined,
    createdAt: rawUser.created ? new Date(rawUser.created * 1000).toString() : undefined,
    permissions,
  };

  // Sometimes we only want a partial user entity. In this case, we want to
  // ensure that any undefined attributes are completely removed from the object.
  // We do, however, want to preserve other "falsy" values such as `null` or
  // `false` as these are still valid values.
  const cleanUser = Object.keys(user).reduce((acc, key) => {
    if (user[key] === undefined) return acc;

    return {
      ...acc,
      [key]: user[key],
    };
  }, {});

  return cleanUser;
};

/**
 * Map list of users returned by server to usable format by front-end UI.
 *
 * @example users (from server)
 * [
 *   {
 *     "id": 123,
 *     "firstName": "Rob",
 *     "lastName": "McLarty",
 *     "email": "rob.mclarty@worktango.com"
 *     "status": "ACTIVE"
 *     "worktangoAdmin": false,
 *     "avatar": "http://somewhere.com/myPhoto.jpg",
 *     "preferredLanguage": {
 *       "id": "1",
 *       "langISOCode": "en-ca"
 *     },
 *     "globalActions": [],
 *     "permissions": []
 *   }
 * ]
 */
const usersFrom = (rawUsers = []) => {
  const users = rawUsers.map((rawUser) => userFrom(rawUser));

  return users;
};

export { usersFrom };
